import { Injectable, inject } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { firstValueFrom, of, timer } from "rxjs";
import { catchError, tap, switchMap, delay } from "rxjs/operators";
import { StorageProxy } from "./storage-proxy.service";
import { LazyTranslateService } from "../../../common/services/lazy-translate.service";

@Injectable({ providedIn: "root" })
export class LanguageService {

	public languages: string[] = ["pt", "en", "es"];

	public idiomas = [
		{ codigo: 'pt', nome: 'Português', bandeira: 'assets/images/flags/brazil.jpg' },
		{ codigo: 'en', nome: 'English', bandeira: 'assets/images/flags/us.jpg' },
		{ codigo: 'es', nome: 'Español', bandeira: 'assets/images/flags/mexico.jpg' }
	  ];

	private lazyTranslate = inject(LazyTranslateService);
	private initPromise?: Promise<void>;

	constructor(public translate: TranslateService) {
		this.initPromise = this.initialize();
	}

	/**
	 * Inicializa o serviço de tradução garantindo que um idioma válido seja carregado antes do bootstrap
	 */
	initialize(): Promise<void> {
		if (this.initPromise) {
			return this.initPromise;
		}

		const resolvedLang = this.resolveInitialLanguage();

		this.translate.addLangs(this.languages);
		// Define o idioma padrão para fallback
		this.translate.setDefaultLang(resolvedLang);

		// Persiste imediatamente para manter consistência com componentes que acessam o storage
		StorageProxy.set('linguagem', resolvedLang);

		this.initPromise = firstValueFrom(
			this.translate.use(resolvedLang).pipe(
				tap(() => {
					// Garante que o idioma atual está sincronizado com o storage
					StorageProxy.set('linguagem', resolvedLang);
				}),
				catchError(err => {
					console.error('Erro ao inicializar idioma padrão', err);
					// Tenta recarregar após um delay se houver erro
					setTimeout(() => {
						this.translate.use(resolvedLang).subscribe({
							next: () => {
								console.log('✓ Traduções recarregadas após erro inicial');
							},
							error: (retryErr) => {
								console.warn('Erro ao recarregar traduções após erro:', retryErr);
							}
						});
					}, 1000);
					return of({});
				})
			)
		).then(() => void 0);

		return this.initPromise;
	}

	private resolveInitialLanguage(): string {
		const stored = StorageProxy.getString('linguagem');
		const browserLang = this.translate.getBrowserLang();
		const candidates = [stored, browserLang, 'pt'];
		const match = candidates.find(lang => lang && /^(pt|en|es|de|it|ru)$/i.test(lang));
		return (match || 'pt').split('-')[0].toLowerCase();
	}

	public setLanguage(lang: string) {
		// Garante que a inicialização foi concluída antes de alterar o idioma
		this.initialize().then(() => {
			StorageProxy.set('linguagem', lang);
			
			// Primeiro muda o idioma principal
			this.translate.setDefaultLang(lang);
			this.translate.use(lang).subscribe({
				next: () => {
					console.log(`✓ Idioma principal alterado para: ${lang}`);
					
					// Depois recarrega as traduções dos módulos já carregados
					this.lazyTranslate.onLangChange(lang).subscribe({
						next: () => {
							console.log(`✓ Todas as traduções recarregadas para o idioma: ${lang}`);
						},
						error: (err) => console.error('Erro ao recarregar traduções dos módulos:', err)
					});
				},
				error: (err) => {
					console.error('Erro ao alterar idioma principal:', err);
				}
			});
		});
		
	}

	public getLanguage()
	{
		const stored = StorageProxy.getString('linguagem') || this.resolveInitialLanguage();
		return this.idiomas.find(objeto => objeto.codigo === stored) ?? this.idiomas[0];
	}
}
